import React, { useEffect, useRef, useState } from 'react';
import { useParams, useLocation, Link } from 'react-router-dom';
import { IPublicClientApplication } from '@azure/msal-browser';
import { AppTheme, Project, HealthRAG, ProjectLifecycleStatus, Task } from '../../types';
import { getProject, updateProjectStatus, getProjectAveragePercentComplete } from '../../services/projectService';
import { getTaskById } from '../../services/taskService';
import { getAccount } from '../../auth/authService';
import { getMyMembership, ProjectMemberRole } from '../../services/projectMemberService';
import { getStakeholderRecord, allowedTabKeys } from '../../services/projectStakeholderService';
import TaskDetailDrawer from '../../task/TaskDetailDrawer';
import ProjectNavBar, { TabKey } from './ProjectNavBar';
import OverviewTab from './tabs/OverviewTab';
import TasksTab from './tabs/TasksTab';
import GoalsTab from './tabs/GoalsTab';
import DocumentsTab from './tabs/DocumentsTab';
import LoopTab from './tabs/LoopTab';
import ActivityTab from './tabs/ActivityTab';
import ReportsTab from './tabs/ReportsTab';
import SettingsTab from './tabs/SettingsTab';
import IssuesTab from './tabs/IssuesTab';
import RisksTab from './tabs/RisksTab';
import MilestonesTab from './tabs/MilestonesTab';
import DecisionsTab from './tabs/DecisionsTab';
import StandupTab from './tabs/StandupTab';
import TimeTab from './tabs/TimeTab';
import BudgetTab from './tabs/BudgetTab';
import ResourcesTab from './tabs/ResourcesTab';
import ChangeRequestsTab from './tabs/ChangeRequestsTab';
import SprintsTab from './tabs/SprintsTab';
import AIAgentTab from './tabs/AIAgentTab';
import ClientPortalTab from './tabs/ClientPortalTab';
import PunchListTab from './tabs/PunchListTab';
import FormsTab from './tabs/FormsTab';
import LessonsTab from './tabs/LessonsTab';
import TrainingTab from './tabs/TrainingTab';
import StakeholderTab from './tabs/StakeholderTab';
import ConstructionTab from './tabs/ConstructionTab';
import SearchPanel from '../../components/search/SearchPanel';

interface Props { pca: IPublicClientApplication; theme: AppTheme; }

const STATUS_LABEL: Record<number, string> = {
  100000000: 'Draft', 100000001: 'Active', 100000002: 'On Hold', 100000003: 'At Risk',
  100000004: 'Complete', 100000005: 'Cancelled', 100000006: 'Archived',
};
// Pill color variant per status (maps to .pill / .pill.amber / .pill.green / .pill.red / .pill.neutral)
const STATUS_PILL_VARIANT: Record<number, string> = {
  [ProjectLifecycleStatus.Draft]:     'neutral',
  [ProjectLifecycleStatus.Active]:    '',         // brand = default .pill
  [ProjectLifecycleStatus.OnHold]:    'amber',
  [ProjectLifecycleStatus.AtRisk]:    'red',
  [ProjectLifecycleStatus.Complete]:  'green',
  [ProjectLifecycleStatus.Cancelled]: 'neutral',
  [ProjectLifecycleStatus.Archived]:  'neutral',
};
const STATUS_OPTIONS = [
  ProjectLifecycleStatus.Draft,
  ProjectLifecycleStatus.Active,
  ProjectLifecycleStatus.OnHold,
  ProjectLifecycleStatus.AtRisk,
  ProjectLifecycleStatus.Complete,
  ProjectLifecycleStatus.Cancelled,
  ProjectLifecycleStatus.Archived,
];
const HEALTH_COLOR: Record<number, string> = {
  [HealthRAG.Green]: 'var(--success)',
  [HealthRAG.Amber]: 'var(--warning)',
  [HealthRAG.Red]:   'var(--danger)',
};

function StatusDropdown({
  status, projectId, pca, onStatusChanged,
}: {
  status: number;
  projectId: string;
  pca: IPublicClientApplication;
  onStatusChanged: (next: number) => void;
}) {
  const [open, setOpen] = useState(false);
  const wrap = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const onDoc = (e: MouseEvent) => {
      if (wrap.current && !wrap.current.contains(e.target as Node)) setOpen(false);
    };
    document.addEventListener('mousedown', onDoc);
    return () => document.removeEventListener('mousedown', onDoc);
  }, []);

  const variantClass = `pill${STATUS_PILL_VARIANT[status] ? ` ${STATUS_PILL_VARIANT[status]}` : ''}`;

  const pick = async (next: number) => {
    if (next === status) { setOpen(false); return; }
    setOpen(false);
    onStatusChanged(next); // optimistic
    try {
      await updateProjectStatus(pca, projectId, next);
    } catch {
      onStatusChanged(status); // revert
    }
  };

  return (
    <div ref={wrap} style={{ position: 'relative' }}>
      <button
        type="button"
        className={variantClass}
        onClick={() => setOpen(o => !o)}
        style={{
          border: 'none', cursor: 'pointer',
          fontFamily: 'var(--font-ui)',
          display: 'inline-flex', alignItems: 'center', gap: 4,
        }}
        aria-haspopup="listbox"
        aria-expanded={open}
      >
        {STATUS_LABEL[status] ?? '—'}
        <span style={{ fontSize: 9, opacity: 0.7 }}>▾</span>
      </button>
      {open && (
        <div style={{
          position: 'absolute', top: '100%', left: 0, marginTop: 4,
          background: 'var(--surface)',
          border: '1px solid var(--border)',
          borderRadius: 'var(--r-md)',
          boxShadow: 'var(--shadow-panel)',
          minWidth: 140, zIndex: 200,
          padding: 4,
        }}>
          {STATUS_OPTIONS.map(v => {
            const selected = v === status;
            return (
              <div
                key={v}
                onClick={() => pick(v)}
                style={{
                  padding: '6px 10px',
                  fontSize: 12,
                  fontWeight: selected ? 500 : 400,
                  color: selected ? 'var(--brand)' : 'var(--ink)',
                  background: selected ? 'var(--brand-mist)' : 'transparent',
                  borderRadius: 'var(--r-sm)',
                  cursor: 'pointer',
                  transition: 'background 0.12s',
                }}
                onMouseEnter={e => !selected && (e.currentTarget.style.background = 'var(--surface-2)')}
                onMouseLeave={e => !selected && (e.currentTarget.style.background = 'transparent')}
              >
                {STATUS_LABEL[v]}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}

export default function ProjectDetail({ pca }: Props) {
  const { projectId = '' } = useParams();
  const [project, setProject] = useState<Project | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError]     = useState<string | null>(null);
  const [active, setActive]   = useState<TabKey>('tasks');
  const [searchOpen, setSearchOpen] = useState(false);
  const [focusTask, setFocusTask] = useState<Task | null>(null);
  const [focusMilestoneId, setFocusMilestoneId] = useState<string | null>(null);
  // Stakeholder nav gating: null = no restriction (PM/TM/Observer or not a
  // stakeholder); a Set = this user is a Stakeholder and may only see these
  // tabs (always includes 'overview').
  const [stakeholderTabs, setStakeholderTabs] = useState<Set<TabKey> | null>(null);
  const location = useLocation();

  // Resolve the current user's role on this project. Only the Stakeholder
  // role is surface-gated; everyone else keeps the standard tab set.
  useEffect(() => {
    if (!projectId) return;
    let cancelled = false;
    (async () => {
      try {
        const acct = await getAccount(pca);
        const oid = acct?.localAccountId ?? acct?.homeAccountId?.split('.')[0] ?? '';
        if (!oid) return;
        const membership = await getMyMembership(pca, projectId, oid);
        if (cancelled) return;
        if (membership?.dagrun_pm_role === ProjectMemberRole.Stakeholder) {
          const rec = await getStakeholderRecord(pca, projectId, oid);
          if (cancelled) return;
          // A stakeholder with no record (or all toggles off) sees only Overview.
          setStakeholderTabs(rec ? allowedTabKeys(rec) : new Set<TabKey>(['overview']));
        } else {
          setStakeholderTabs(null);
        }
      } catch { /* on error, leave unrestricted */ }
    })();
    return () => { cancelled = true; };
  }, [pca, projectId]);

  // Cross-project search (and other deep links) navigate here with a target
  // tab in router state. Land on that tab when present, and — when a task
  // was chosen — open its Task Detail Panel. Two entry shapes are handled:
  //   • router state { tab, focusId, focusKind } — global search results
  //   • ?taskId=<guid> query param — Teams notification / activity deep links
  // For a task the panel opens regardless of same- vs cross-project, since
  // navigate() always changes location.key and this effect re-runs.
  useEffect(() => {
    const state = location.state as { tab?: TabKey; focusId?: string; focusKind?: string } | null;
    if (state?.tab) setActive(state.tab);

    // Milestone deep link (slip / milestone-approval notifications): open the
    // Milestones tab and focus the specific milestone (opens its drawer, which
    // hosts the EntityApprovalSection).
    if (state?.focusKind === 'milestone' && state.focusId) {
      setActive('milestones');
      setFocusMilestoneId(state.focusId);
      return;
    }

    const taskIdFromQuery = new URLSearchParams(location.search).get('taskId');
    const targetTaskId =
      (state?.focusKind === 'task' && state.focusId) ? state.focusId : (taskIdFromQuery || null);
    if (!targetTaskId) return;

    let cancelled = false;
    getTaskById(pca, targetTaskId)
      .then(t => { if (!cancelled) { setFocusTask(t); setActive('tasks'); } })
      .catch(() => { /* task deleted / not accessible — leave the board as-is */ });
    return () => { cancelled = true; };
  }, [location.key]); // eslint-disable-line react-hooks/exhaustive-deps

  const reload = async () => {
    try {
      setLoading(true);
      // Project row + task-derived completion % in parallel. Dataverse
      // doesn't auto-roll-up task percentcomplete onto the project
      // record, so the stored dagrun_pm_percentcomplete is always stale
      // (typically 0). We recompute it client-side here and overlay it
      // on the loaded project so the header and progress bar reflect
      // live task state.
      const [p, avg] = await Promise.all([
        getProject(pca, projectId),
        getProjectAveragePercentComplete(pca, projectId),
      ]);
      setProject({ ...p, dagrun_pm_percentcomplete: avg });
    } catch (e) { setError(e instanceof Error ? e.message : 'Failed to load project'); }
    finally { setLoading(false); }
  };

  useEffect(() => { if (projectId) reload(); }, [projectId]); // eslint-disable-line

  if (loading) return <div className="page"><div className="spinner" style={{ margin: '80px auto' }} /></div>;
  if (error)   return <div className="page"><div className="card" style={{ padding: 20, color: 'var(--danger)' }}>{error}</div></div>;
  if (!project) return <div className="page">Project not found.</div>;

  // A stakeholder who lands on (or deep-links to) a tab they can't see is
  // shown Overview instead — the always-available baseline.
  const effectiveActive: TabKey = (stakeholderTabs && !stakeholderTabs.has(active)) ? 'overview' : active;
  const changeTab = (t: TabKey) => { if (!stakeholderTabs || stakeholderTabs.has(t)) setActive(t); };

  const renderTab = () => {
    switch (effectiveActive) {
      case 'overview':    return <OverviewTab pca={pca} project={project} onNavigate={changeTab} onProjectChanged={patch => setProject(p => p ? { ...p, ...patch } : p)} />;
      case 'tasks':       return <TasksTab pca={pca} projectId={projectId} project={project} />;
      case 'goals':       return <GoalsTab pca={pca} projectId={projectId} />;
      case 'documents':   return <DocumentsTab pca={pca} projectId={projectId} project={project} />;
      case 'forms':       return <FormsTab pca={pca} projectId={projectId} />;
      case 'loop':        return <LoopTab project={project} />;
      case 'activity':    return <ActivityTab pca={pca} projectId={projectId} />;
      case 'reports':     return <ReportsTab pca={pca} projectId={projectId} project={project} />;
      case 'settings':    return <SettingsTab pca={pca} project={project} onSaved={reload} />;
      case 'issues':      return <IssuesTab pca={pca} projectId={projectId} project={project} />;
      case 'risks':       return <RisksTab pca={pca} projectId={projectId} project={project} />;
      case 'milestones':  return <MilestonesTab pca={pca} projectId={projectId} project={project} focusMilestoneId={focusMilestoneId} onFocusConsumed={() => setFocusMilestoneId(null)} />;
      case 'decisions':   return <DecisionsTab pca={pca} projectId={projectId} />;
      case 'time':        return <TimeTab pca={pca} projectId={projectId} project={project} />;
      case 'budget':      return <BudgetTab pca={pca} projectId={projectId} project={project} />;
      case 'resources':   return <ResourcesTab pca={pca} projectId={projectId} project={project} />;
      case 'changes':     return <ChangeRequestsTab pca={pca} projectId={projectId} project={project} />;
      case 'sprints':     return <SprintsTab pca={pca} projectId={projectId} project={project} />;
      case 'standup':     return <StandupTab pca={pca} projectId={projectId} project={project} />;
      case 'aiagent':     return <AIAgentTab pca={pca} projectId={projectId} />;
      case 'clientportal':return <ClientPortalTab pca={pca} projectId={projectId} />;
      case 'punchlist':   return <PunchListTab pca={pca} projectId={projectId} project={project} />;
      case 'lessons':     return <LessonsTab pca={pca} projectId={projectId} project={project} />;
      case 'submittalrfi': return <ConstructionTab pca={pca} projectId={projectId} project={project} />;
      case 'training':    return <TrainingTab pca={pca} projectId={projectId} project={project} />;
      case 'stakeholders': return <StakeholderTab pca={pca} projectId={projectId} project={project} />;
      default:            return null;
    }
  };

  return (
    <div style={{ display: 'flex', flexDirection: 'column', minHeight: 'calc(100vh - 56px)' }}>
      {/* Project header. The back arrow at the head of the title row
          IS the in-app navigation back to the projects listing —
          ProjectDetail wraps every project sub-route, so this Link is
          visible from every view (board / list / gantt / settings /
          milestones / etc.) in Teams desktop, Teams browser, and
          standalone. The Teams staticTab focusEnter handler in App.tsx
          is a best-effort backup; this Link is the load-bearing path. */}
      <div style={{
        background: 'var(--surface)',
        borderBottom: '1px solid var(--border)',
        padding: '14px 32px 16px 32px',
        borderLeft: '4px solid var(--brand)',
      }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 16 }}>
          <div style={{ flex: 1, minWidth: 0 }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
              {/* "← All Projects | {Project Name}" rendered as a single
                  inline-flex group so the pipe's own horizontal padding
                  controls the spacing between link and title without
                  competing with the outer flex `gap` that drives the
                  spacing between the title group and the health/status
                  badges to the right. The link inherits the title's
                  display font/size/weight and only differs in color
                  (brand teal) so it reads as a peer of the title. */}
              <div style={{ display: 'inline-flex', alignItems: 'center' }}>
                <Link
                  to="/portfolio"
                  style={{
                    fontFamily: 'var(--font-display)',
                    fontSize: 22, fontWeight: 300, lineHeight: 1.2,
                    color: '#0E7490',
                    textDecoration: 'none',
                    display: 'inline-flex', alignItems: 'center', gap: 6,
                    flexShrink: 0,
                    transition: 'opacity 0.15s',
                  }}
                  onMouseEnter={e => (e.currentTarget.style.opacity = '0.8')}
                  onMouseLeave={e => (e.currentTarget.style.opacity = '1')}
                  title="Back to all projects"
                  aria-label="Back to all projects"
                >
                  <span aria-hidden>←</span>
                  <span>All Projects</span>
                </Link>
                <span aria-hidden style={{
                  padding: '0 12px',
                  fontFamily: 'var(--font-display)',
                  fontSize: 22, fontWeight: 300, lineHeight: 1.2,
                  color: 'var(--ink-4)',
                }}>|</span>
                <strong style={{ fontFamily: 'var(--font-display)', fontSize: 22, color: 'var(--ink)', fontWeight: 300 }}>
                  {project.dagrun_pm_name}
                </strong>
              </div>
              {project.dagrun_pm_healthrag != null && (
                <span title="Health" style={{
                  width: 10, height: 10, borderRadius: 5,
                  background: HEALTH_COLOR[project.dagrun_pm_healthrag] ?? 'var(--ink-4)',
                }} />
              )}
              <StatusDropdown
                status={project.dagrun_pm_lifecyclestatus}
                projectId={projectId}
                pca={pca}
                onStatusChanged={next => setProject(p => p ? { ...p, dagrun_pm_lifecyclestatus: next } : p)}
              />
            </div>
            {project.dagrun_pm_description && (
              <div style={{ fontSize: 13, color: 'var(--ink-3)', marginTop: 4, fontWeight: 300 }}>
                {project.dagrun_pm_description}
              </div>
            )}
          </div>
          <div style={{ textAlign: 'right', minWidth: 180 }}>
            <div style={{ fontSize: 11, color: 'var(--ink-4)', fontFamily: 'var(--font-mono)' }}>
              {project.dagrun_pm_percentcomplete ?? 0}% complete
            </div>
            <div style={{ background: 'var(--surface-2)', borderRadius: 3, height: 4, overflow: 'hidden', marginTop: 4 }}>
              <div style={{ height: '100%', width: `${project.dagrun_pm_percentcomplete ?? 0}%`, background: 'var(--brand)' }} />
            </div>
          </div>
        </div>
      </div>

      <ProjectNavBar project={project} active={effectiveActive} onChange={changeTab} restrictTabs={stakeholderTabs} onSearchOpen={() => setSearchOpen(true)} />

      <div style={{ flex: 1, overflow: 'auto' }}>{renderTab()}</div>

      {searchOpen && (
        <SearchPanel
          pca={pca}
          projectId={projectId}
          projectName={project.dagrun_pm_name}
          onClose={() => setSearchOpen(false)}
        />
      )}

      {/* Task Detail Panel opened from global search or a task deep link. */}
      <TaskDetailDrawer
        task={focusTask}
        pca={pca}
        onClose={() => setFocusTask(null)}
        onSaved={reload}
      />
    </div>
  );
}
